import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, dashboardPathForRole } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { evaluatePassword } from "../lib/passwordPolicy";
import { useAntiBot, HoneypotField, TurnstileWidget } from "../components/auth/AntiBotFields";

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const antiBot = useAntiBot();

  React.useEffect(() => {
    if (user) navigate(dashboardPathForRole(user.role), { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (!accepted) {
      setError("Debes aceptar los Términos y la Política de Tratamiento de Datos.");
      return;
    }
    if (antiBot.captchaConfig?.enabled && antiBot.captchaConfig?.required_register && !antiBot.captchaToken) {
      setError("Completa la verificación anti-bot.");
      return;
    }
    setSubmitting(true);
    const res = await register({
      name: name.trim(),
      email: email.trim(),
      password,
      accepted_terms: true,
      honeypot: antiBot.honeypot,
      form_loaded_at: antiBot.formLoadedAt,
      captcha_token: antiBot.captchaToken || undefined,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      antiBot.resetCaptcha();
      return;
    }
    // Iter25.f — Si pending_verification, ir a la página de espera.
    if (res.pending_verification) {
      toast.success("Cuenta creada. Verifica tu email para continuar.");
      navigate("/verify-email-pending", {
        state: { email: res.user.email, fromRegister: true },
        replace: true,
      });
      return;
    }
    toast.success("Cuenta creada. ¡Bienvenido!");
    navigate(dashboardPathForRole(res.user.role), { replace: true });
  };

  return (
    <div className="container-prose py-20">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="JustSapiens Procure — Inicio">
            <img
              src="/brand/logo-stacked-positive.png"
              alt="JustSapiens Procure"
              className="h-32 w-auto object-contain"
              loading="eager"
              decoding="async"
            />
          </Link>
        </div>
        <div className="border border-gray-200 bg-white p-8">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-3">Registro</p>
          <h1 className="font-serif text-3xl text-gray-900 mb-2">Crear cuenta</h1>
          <p className="text-sm text-gray-600">
            Empieza con la prevalidación gratuita Explorador.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
          <div className="space-y-1.5">
            <Label htmlFor="reg-name">Nombre completo</Label>
            <Input
              id="reg-name"
              required
              minLength={2}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input"
              className="rounded-sm h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-email">Correo electrónico</Label>
            <Input
              id="reg-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="rounded-sm h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reg-password">Contraseña</Label>
            <Input
              id="reg-password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input"
              className="rounded-sm h-11"
            />
            <PasswordStrengthMeter value={password} testIdPrefix="register-pwd" />
          </div>

          <div className="flex items-start gap-3 pt-1">
            <Checkbox
              id="reg-terms"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(v === true)}
              data-testid="register-terms-checkbox"
              className="rounded-sm mt-1"
            />
            <Label htmlFor="reg-terms" className="text-sm text-gray-700 leading-snug font-normal">
              Acepto los{" "}
              <Link to="/terminos" className="text-primary hover:underline">Términos</Link>{" "}
              y la{" "}
              <Link to="/politica-datos" className="text-primary hover:underline">
                Política de Tratamiento de Datos
              </Link>{" "}
              (Ley 1581 de 2012).
            </Label>
          </div>

          {error && (
            <div
              data-testid="register-error"
              className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2"
            >
              {error}
            </div>
          )}

          <HoneypotField value={antiBot.honeypot} onChange={antiBot.setHoneypot} />
          <TurnstileWidget
            config={antiBot.captchaConfig}
            onToken={antiBot.setCaptchaToken}
            action="register"
          />

          <Button
            type="submit"
            disabled={submitting || !evaluatePassword(password).isValid || !accepted}
            className="w-full rounded-sm h-11"
            data-testid="register-submit-button"
          >
            {submitting ? "Creando cuenta…" : "Crear cuenta"}
          </Button>
        </form>

        <p className="text-sm text-gray-600 mt-6 text-center">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" data-testid="register-go-login" className="text-primary hover:underline">
            Iniciar sesión
          </Link>
        </p>
        </div>
      </div>
    </div>
  );
}
